import { X, ShoppingCart, Trash2, Zap } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useCart } from '@/contexts/CartContext';
import { toast } from 'sonner';
import { useState } from 'react';

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CartDrawer({ isOpen, onClose }: CartDrawerProps) {
  const { sessionId, setCartCount } = useCart();
  const utils = trpc.useUtils();
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  const { data: cartData, isLoading } = trpc.cart.get.useQuery(
    { sessionId },
    { enabled: isOpen }
  );

  const removeMutation = trpc.cart.remove.useMutation({
    onSuccess: () => {
      utils.cart.get.invalidate();
      const newCount = (cartData?.employees?.length || 1) - 1;
      setCartCount(Math.max(0, newCount));
    },
  });

  const checkoutMutation = trpc.orders.checkout.useMutation({
    onSuccess: (data) => {
      if (data.checkoutUrl) {
        toast.success('Redirecting to secure Stripe checkout...');
        window.open(data.checkoutUrl, '_blank');
      }
      setIsCheckingOut(false);
    },
    onError: (err) => {
      toast.error(`Checkout failed: ${err.message}`);
      setIsCheckingOut(false);
    },
  });

  const handleCheckout = () => {
    setIsCheckingOut(true);
    checkoutMutation.mutate({ sessionId });
  };

  const employees = cartData?.employees || [];
  const total = cartData?.total || 0;
  const subtotal = cartData?.subtotal || 0;
  const discount = cartData?.discountAmount || 0;

  // Determine which discount applies — SALE50 always wins over BUNDLE20
  const SITEWIDE_SALE_ACTIVE = true;
  const hasFullBundle = (() => {
    if (!cartData?.employees) return false;
    const deptCounts: Record<string, number> = {};
    for (const emp of cartData.employees) {
      if (!emp) continue;
      deptCounts[(emp as any).departmentId] = (deptCounts[(emp as any).departmentId] || 0) + 1;
    }
    // If any dept has 5+ agents (heuristic for full bundle), flag it
    return Object.values(deptCounts).some(c => c >= 5);
  })();
  // Live Stripe coupon IDs (must match server/routers.ts)
  const LIVE_SALE_COUPON_ID = 'XCBR4ht5';
  const LIVE_BUNDLE_COUPON_ID = 'YX10Mg3y';
  const activeCoupon = SITEWIDE_SALE_ACTIVE ? LIVE_SALE_COUPON_ID : hasFullBundle ? LIVE_BUNDLE_COUPON_ID : null;
  const discountPercent = SITEWIDE_SALE_ACTIVE ? 50 : hasFullBundle ? 20 : 0;
  // Compute the actual discount amount from Stripe coupon (overrides old bundle-only calc)
  const stripeDiscountAmount = activeCoupon ? Math.round(subtotal * discountPercent / 100 * 100) / 100 : discount;
  const displayTotal = activeCoupon ? Math.round((subtotal - stripeDiscountAmount) * 100) / 100 : total;

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[400]"
          style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)' }}
          onClick={onClose}
        />
      )}

      {/* Drawer */}
      <div className={`cart-drawer ${isOpen ? 'open' : ''}`} style={{ zIndex: 500 }}>
        {/* Header */}
        <div style={{
          padding: '1.25rem 1.5rem',
          borderBottom: '1px solid rgba(0,166,235,0.25)',
          background: 'linear-gradient(90deg, rgba(0,166,235,0.08) 0%, transparent 100%)',
        }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ShoppingCart size={18} color="#00A6EB" />
              <div>
                <div className="font-orbitron font-bold text-sm" style={{ color: '#E8F4FF', letterSpacing: '0.1em' }}>
                  DATA CORE
                </div>
                <div className="font-mono-cyber text-xs" style={{ color: 'rgba(0,166,235,0.6)', letterSpacing: '0.1em' }}>
                  {employees.length} AGENT{employees.length !== 1 ? 'S' : ''} QUEUED
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{ color: '#FF0055', padding: 4 }}
              onMouseEnter={e => (e.currentTarget.style.color = '#FF3377')}
              onMouseLeave={e => (e.currentTarget.style.color = '#FF0055')}
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* ── STICKY CHECKOUT BUTTON (always visible when cart has items) ── */}
        {employees.length > 0 && (
          <div style={{
            padding: '1rem 1.5rem',
            borderBottom: '1px solid rgba(0,166,235,0.15)',
            background: 'rgba(0,0,0,0.4)',
          }}>
            {/* Total line */}
            <div className="flex justify-between items-center mb-3">
              <span className="font-orbitron text-xs" style={{ color: 'rgba(232,244,255,0.5)', letterSpacing: '0.1em' }}>
                {activeCoupon ? (
                  <>
                    <span style={{ textDecoration: 'line-through', color: 'rgba(232,244,255,0.3)', marginRight: '0.5rem' }}>${subtotal.toFixed(2)}</span>
                    <span style={{ color: '#FF2D78', fontWeight: 900, letterSpacing: '0.08em' }}>
                      {activeCoupon === LIVE_SALE_COUPON_ID ? '⚡ 50% SALE' : '◈ 20% BUNDLE'} −${stripeDiscountAmount.toFixed(2)}
                    </span>
                  </>
                ) : `${employees.length} AGENT${employees.length !== 1 ? 'S' : ''}`}
              </span>
              <span className="font-orbitron font-black text-xl" style={{ color: activeCoupon ? '#FF2D78' : '#00A6EB' }}>
                ${displayTotal.toFixed(2)}
              </span>
            </div>
            {/* BIG CHECKOUT BUTTON */}
            <button
              onClick={handleCheckout}
              disabled={isCheckingOut}
              style={{
                width: '100%',
                background: isCheckingOut
                  ? 'rgba(0,166,235,0.3)'
                  : 'linear-gradient(135deg, #00A6EB 0%, #0066FF 50%, #FF0055 100%)',
                border: 'none',
                borderRadius: 6,
                padding: '1rem 1.5rem',
                cursor: isCheckingOut ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem',
                fontFamily: 'Orbitron, monospace',
                fontWeight: 900,
                fontSize: '1rem',
                letterSpacing: '0.15em',
                color: '#fff',
                boxShadow: '0 0 30px rgba(0,166,235,0.5), 0 4px 20px rgba(255,0,85,0.3)',
                transition: 'all 0.2s cubic-bezier(0.23,1,0.32,1)',
                textTransform: 'uppercase',
              }}
              onMouseEnter={e => {
                if (!isCheckingOut) {
                  (e.currentTarget as HTMLElement).style.transform = 'scale(1.02)';
                  (e.currentTarget as HTMLElement).style.boxShadow = '0 0 50px rgba(0,166,235,0.7), 0 8px 30px rgba(255,0,85,0.4)';
                }
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.transform = 'scale(1)';
                (e.currentTarget as HTMLElement).style.boxShadow = '0 0 30px rgba(0,166,235,0.5), 0 4px 20px rgba(255,0,85,0.3)';
              }}
              onMouseDown={e => { (e.currentTarget as HTMLElement).style.transform = 'scale(0.97)'; }}
              onMouseUp={e => { (e.currentTarget as HTMLElement).style.transform = 'scale(1.02)'; }}
            >
              {isCheckingOut ? (
                <>
                  <div className="cyber-spinner" style={{ width: 18, height: 18, borderWidth: 2 }} />
                  PROCESSING PAYMENT...
                </>
              ) : (
                <>
                  <Zap size={18} fill="currentColor" />
                  CHECKOUT NOW
                </>
              )}
            </button>
            <div style={{ textAlign: 'center', marginTop: '0.5rem', fontFamily: 'Share Tech Mono, monospace', fontSize: '0.55rem', color: 'rgba(232,244,255,0.3)', letterSpacing: '0.12em' }}>
              🔒 SECURED BY STRIPE · INSTANT DIGITAL DELIVERY
            </div>
          </div>
        )}

        {/* Items */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '1rem' }}>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="cyber-spinner" />
            </div>
          ) : employees.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <ShoppingCart size={40} color="rgba(0,166,235,0.3)" />
              <div className="font-orbitron text-sm text-center" style={{ color: 'rgba(232,244,255,0.4)', letterSpacing: '0.1em' }}>
                DATA CORE EMPTY
              </div>
              <div className="font-rajdhani text-sm text-center" style={{ color: 'rgba(232,244,255,0.3)' }}>
                Browse departments and recruit your AI workforce
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {employees.map((emp) => {
                if (!emp) return null;
                return (
                  <div
                    key={emp.id}
                    className="holo-card"
                    style={{ padding: '0.75rem', borderRadius: 4 }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      {/* Avatar with headshot */}
                      <div
                        style={{
                          width: 44, height: 44, flexShrink: 0,
                          borderRadius: 3,
                          overflow: 'hidden',
                          border: '1px solid rgba(0,166,235,0.4)',
                          backgroundImage: emp.photoUrl ? `url(${emp.photoUrl})` : undefined,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          backgroundColor: 'rgba(0,166,235,0.1)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        {!emp.photoUrl && (
                          <span className="font-orbitron font-black text-xs" style={{ color: '#00A6EB' }}>
                            {emp.name.slice(0, 2)}
                          </span>
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="font-orbitron font-bold text-xs" style={{ color: '#E8F4FF', letterSpacing: '0.05em' }}>
                          {emp.name}
                        </div>
                        <div className="font-rajdhani text-xs mt-0.5" style={{ color: 'rgba(232,244,255,0.5)' }}>
                          {emp.roleTitle}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <div className="font-orbitron font-bold text-sm" style={{ color: '#00A6EB' }}>
                          ${parseFloat(emp.price).toFixed(2)}
                        </div>
                        <button
                          onClick={() => {
                            removeMutation.mutate({ sessionId, employeeId: emp.id });
                            toast.info(`${emp.name} removed from Data Core`);
                          }}
                          style={{ color: 'rgba(255,0,85,0.5)', padding: 2 }}
                          onMouseEnter={e => (e.currentTarget.style.color = '#FF0055')}
                          onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,0,85,0.5)')}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer — ROI calculator + secondary checkout */}
        {employees.length > 0 && (
          <div style={{
            padding: '1rem 1.5rem',
            borderTop: '1px solid rgba(0,166,235,0.15)',
            background: 'rgba(0,0,0,0.3)',
          }}>
            {/* ROI Savings Calculator */}
            {(() => {
              const avgHumanCost = 110000;
              const roiSavings = employees.length * avgHumanCost - total;
              const roiMultiple = roiSavings > 0 ? Math.round(roiSavings / Math.max(total, 1)) : 0;
              return (
                <div className="holo-card mb-3" style={{ padding: '0.75rem', borderColor: 'rgba(0,255,136,0.3)' }}>
                  <div className="font-mono-cyber text-xs mb-1" style={{ color: '#00FF88', letterSpacing: '0.1em' }}>⬡ ROI PROJECTION</div>
                  <div className="flex justify-between items-center">
                    <span className="font-rajdhani text-xs" style={{ color: 'rgba(232,244,255,0.6)' }}>
                      vs. {employees.length} human hire{employees.length > 1 ? 's' : ''}/yr
                    </span>
                    <span className="font-orbitron font-bold text-sm" style={{ color: '#00FF88' }}>
                      ~${(roiSavings / 1000).toFixed(0)}K saved
                    </span>
                  </div>
                  {roiMultiple > 1 && (
                    <div className="font-mono-cyber text-xs mt-1" style={{ color: 'rgba(0,255,136,0.6)' }}>
                      {roiMultiple}x return on investment
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </>
  );
}
